import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Button, Tabs, TextField } from '../ui';
import { apiClient } from '../../api/client';
import { useSession } from '../../context/SessionContext';
import { usePlatform } from '../../context/PlatformContext';
import { SOURCES } from './constants';
import { classifyError } from './errors';
import DocumentLibrary from './DocumentLibrary';
import FileTree from './FileTree';
import PanelState from './PanelState';
import SymbolResults from './SymbolResults';

/**
 * The explorer rail: one search field across all three knowledge sources plus
 * the navigation for each (spec §9.5, §10.5).
 *
 * The search field filters files and documents live and runs a CKB symbol
 * search on submit — so a single control genuinely searches "across them"
 * rather than three disconnected boxes.
 *
 * @param {object} props
 * @param {'files'|'documents'|'symbols'} props.source
 * @param {(source: string) => void} props.onSourceChange
 * @param {import('./selection').KnowledgeSelection|null} props.selection
 * @param {(selection: import('./selection').KnowledgeSelection) => void} props.onSelect
 */
export function ExplorerRail({ source, onSourceChange, selection, onSelect }) {
  const { t } = useTranslation();
  const { sessionId, isConnected, isConnecting, isRestoring, createSession } = useSession();
  const { sources } = usePlatform();

  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [tree, setTree] = useState(null);
  const [treeError, setTreeError] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const autoConnectAttempted = useRef(false);

  const repos = sources?.repos?.enabled ? sources.repos.repositories || [] : [];

  // Auto-connect to the first configured repository once. The ref guard keeps
  // a failed attempt from looping.
  useEffect(() => {
    if (isRestoring || isConnected || isConnecting) return;
    if (autoConnectAttempted.current || repos.length === 0) return;
    autoConnectAttempted.current = true;
    createSession(repos[0].path).catch((err) => setConnectError(err));
  }, [isRestoring, isConnected, isConnecting, repos, createSession]);

  const loadTree = useCallback(() => {
    if (!sessionId || !isConnected) return undefined;
    let cancelled = false;
    setTreeLoading(true);
    setTreeError(null);
    apiClient
      .getFileTree(sessionId)
      .then((result) => {
        if (!cancelled) setTree(result.tree || []);
      })
      .catch((err) => {
        if (!cancelled) setTreeError(err);
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, isConnected]);

  useEffect(() => loadTree(), [loadTree]);

  const retryConnect = () => {
    if (repos.length === 0) return;
    setConnectError(null);
    autoConnectAttempted.current = true;
    createSession(repos[0].path).catch((err) => setConnectError(err));
  };

  const renderFiles = () => {
    if (isRestoring || isConnecting) {
      return (
        <PanelState
          status="loading"
          title={repos[0] ? t('ide.connectingTo', { name: repos[0].name }) : t('states.loading')}
          compact
        />
      );
    }
    if (connectError) {
      return (
        <PanelState
          status={classifyError(connectError)}
          title={t('ide.connectFailed')}
          error={connectError}
          onRetry={retryConnect}
          compact
        />
      );
    }
    if (!isConnected) {
      return (
        <PanelState
          status="noSource"
          title={repos.length === 0 ? t('ide.connectPromptNoRepos') : t('ide.connectPrompt')}
          onRetry={repos.length > 0 ? retryConnect : undefined}
          compact
        />
      );
    }
    if (treeLoading && !tree) return <PanelState status="loading" compact />;
    if (treeError) {
      return <PanelState status={classifyError(treeError)} error={treeError} onRetry={loadTree} compact />;
    }
    if (!tree) return <PanelState status="loading" compact />;

    return (
      <FileTree
        tree={tree}
        query={query}
        label={t('knowledge.files')}
        selectedPath={selection?.path || null}
        onSelect={(item) => onSelect?.({ item, kind: item.type === 'directory' ? 'directory' : 'file' })}
      />
    );
  };

  const items = [
    {
      id: SOURCES.files,
      label: t('knowledge.files'),
      panel: source === SOURCES.files ? renderFiles() : null
    },
    {
      id: SOURCES.documents,
      label: t('knowledge.documents'),
      panel:
        source === SOURCES.documents ? (
          <DocumentLibrary
            query={query}
            selectedId={selection?.kind === 'document' ? selection.id : null}
            onSelect={(doc) => onSelect?.({ item: doc, kind: 'document' })}
          />
        ) : null
    },
    {
      id: SOURCES.symbols,
      label: t('knowledge.symbols'),
      panel:
        source === SOURCES.symbols ? (
          <SymbolResults
            query={submittedQuery}
            selectedId={selection?.kind === 'symbol' ? selection.id : null}
            onSelect={(symbol) => onSelect?.({ item: symbol, kind: 'symbol' })}
          />
        ) : null
    }
  ];

  return (
    <div className="etch-knowledge__rail">
      <form
        className="etch-knowledge__rail-search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmittedQuery(query.trim());
          if (query.trim()) onSourceChange?.(SOURCES.symbols);
        }}
      >
        <TextField
          type="search"
          label={t('knowledge.search')}
          hideLabel
          placeholder={t('knowledge.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button type="submit" variant="secondary" size="compact" disabled={!query.trim()}>
          <Search size={16} aria-hidden="true" />
          <span className="etch-visually-hidden">{t('knowledge.search')}</span>
        </Button>
      </form>

      <Tabs
        className="etch-knowledge__rail-tabs"
        ariaLabel={t('knowledge.sourceSwitcher')}
        items={items}
        value={source}
        onChange={onSourceChange}
      />
    </div>
  );
}

export default ExplorerRail;
